<template>
  <div class="page-wrapper">
    <div class="container py-5">
      <h2 class="mb-4">Chỉnh sửa bài viết</h2>

      <div v-if="loading" class="alert alert-info">Đang tải dữ liệu...</div>
      <div v-if="error" class="alert alert-danger">{{ error }}</div>

      <form v-else-if="article" @submit.prevent="handleSubmit" class="card p-4 shadow-sm">
        <div class="mb-3">
          <label class="form-label">Tiêu đề:</label>
          <input v-model="title" type="text" class="form-control" required />
        </div>
        
        <div class="mb-3">
          <label class="form-label">Tiêu đề SEO:</label>
          <input v-model="seoTitle" type="text" class="form-control" />
        </div>

        <div class="mb-3">
          <label class="form-label">Mô tả:</label>
          <textarea v-model="description" class="form-control" rows="3" required />
        </div>
        
        <!-- <div class="mb-3">
          <label class="form-label">Ảnh đại diện:</label>
          <div class="d-flex align-items-center gap-2">
            <button type="button" class="btn btn-secondary btn-sm" @click="openAvatarModal">
              Chọn ảnh
            </button>
            <div v-if="images" class="avatar-preview ms-3">
              <img :src="images" alt="Ảnh đại diện" class="img-thumbnail" style="max-width: 200px;" />
            </div>
          </div>
        </div> -->
        
        <div class="mb-3">
          <label class="form-label">Ảnh đại diện:</label>
          <div class="d-flex align-items-center gap-2">
            <button type="button" class="btn btn-secondary btn-sm" @click="openAvatarModal">
              Chọn ảnh (File Manager)
            </button>

            <label class="btn btn-outline-primary btn-sm mb-0">
              Tải ảnh lên WP
              <input type="file" accept="image/*" class="d-none" @change="onAvatarFilePicked" />
            </label>

            <div v-if="images" class="avatar-preview ms-3">
              <img :src="images" alt="Ảnh đại diện" class="img-thumbnail" style="max-width: 200px;" />
            </div>
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label">Từ khóa SEO:</label>
          <input v-model="focusKeywords" type="text" class="form-control" />
        </div>

        <div class="mb-3" v-if="seoScore !== null && seoScore !== undefined">
          <p><strong>Điểm SEO Rank Math:</strong> 
            <span :class="['badge',
              seoScoreDisplay >= 80 ? 'badge-success'
              : seoScoreDisplay >= 50 ? 'badge-warning'
              : 'badge-secondary'
            ]">
          {{ seoScoreDisplay }}/100
        </span>
          </p>
        </div>

        <div class="mb-3">
          <label class="form-label">Slug (URL tùy chỉnh):</label>
          <input v-model="slug" type="text" class="form-control" placeholder="ten-bai-viet" />
        </div>

        <div class="mb-3">
          <label class="form-label">Nội dung bài viết:</label>
          <Editor
            v-model="content"
            api-key="yrhpcam8qdug0kvk57vkx6kqcvs0qik1uatwa80yhh12nk1x"
            :init="tinyInit"
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Trạng thái bài viết:</label>
          <select v-model="status" class="form-select">
            <option value="draft">Bản nháp</option>
            <option value="pending">Chờ xét duyệt</option>
            <option value="publish">Công khai</option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label">Chọn danh mục:</label>
          <div v-if="categoriesLoading" class="text-info">Đang tải danh mục...</div>
          <div v-else>
            <div class="form-check" v-for="category in categories" :key="category.id">
              <input
                class="form-check-input"
                type="checkbox"
                :value="category.id"
                :id="`cat-${category.id}`"
                v-model="selectedCategories"
              />
              <label class="form-check-label" :for="`cat-${category.id}`">
                {{ category.name }}
              </label>
            </div>
          </div>
        </div>

        <button class="btn btn-primary" :disabled="submitting || !canSubmit">
          {{ submitting ? 'Đang lưu...' : 'Hoàn tất và lưu bài viết' }}
        </button>
      </form>
      <div v-else>Không tìm thấy dữ liệu bài viết.</div>
    </div>
    
    <FileManagerModal 
      v-if="showFileManagerModal" 
      :folderTabs="imageTabs"
      @close="showFileManagerModal = false"
      @file-selected="onFileSelected"
      @tab-created="onTabCreated"
      @tab-deleted="onTabDeleted"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import api from '../api';
import Editor from '@tinymce/tinymce-vue';
import { useToast } from "vue-toastification";
import FileManagerModal from '@/components/FileManagerModal.vue';

const route = useRoute();
const wpPostId = route.params.id;
const wpSiteId = route.params.wpSiteId;

const toast = useToast();

const article = ref(null);
const title = ref('');
const seoTitle = ref('');
const description = ref('');
const images = ref('');
const featuredMediaId = ref(null); // ID media WP

const focusKeywords = ref('');
const slug = ref('');
const content = ref('');
const seoScore = ref(null);

const categories = ref([]);
const selectedCategories = ref([]);
const status = ref('draft');
const categoriesLoading = ref(true);

const loading = ref(true);
const submitting = ref(false);
const error = ref('');

// --- File Manager Modal State ---
const showFileManagerModal = ref(false);
const fileManagerCallback = ref(null);
const imageTabs = ref([]);
const targetField = ref(null);

// ---------- Helpers ----------
const fetchFolders = async () => {
  try {
    const res = await api.get('/api/images/folders');
    imageTabs.value = (res.data || []).map((folderName, index) => ({
      id: (index + 1).toString(),
      name: folderName,
      folderName,
    }));
  } catch {
    imageTabs.value = [];
    toast.error('Lỗi khi tải danh sách folder.');
  }
};

const openAvatarModal = async () => {
  targetField.value = 'avatar';
  await fetchFolders();
  showFileManagerModal.value = true;
};

const onTabCreated = (newTab) => { imageTabs.value.push(newTab); };
const onTabDeleted = (tabIdToDelete) => {
  imageTabs.value = imageTabs.value.filter(tab => tab.id !== tabIdToDelete);
};

const onFileSelected = async (image) => {
  if (!image || !image.url) {
    toast.error('Không thể chọn ảnh, URL không hợp lệ.');
    return;
  }
  if (targetField.value === 'avatar') {
    images.value = image.url; // Gửi URL lên BE, BE resolve -> featured_media
    toast.success('Ảnh đại diện đã chọn!');
  } else if (targetField.value === 'content' && fileManagerCallback.value) {
    fileManagerCallback.value(image.url, { alt: image.title, title: image.title });
  }
  showFileManagerModal.value = false;
  fileManagerCallback.value = null;
  targetField.value = null;
};

const tinyInit = {
  height: 500,
  menubar: true,
  plugins: [
    'advlist', 'autolink', 'link', 'image', 'lists', 'charmap', 'preview', 'anchor',
    'searchreplace', 'visualblocks', 'code', 'fullscreen',
    'insertdatetime', 'media', 'table', 'paste', 'help', 'wordcount'
  ],
  toolbar: [
    'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough forecolor backcolor',
    'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media',
    'removeformat | table | code fullscreen | preview print | help'
  ],
  relative_urls: false,
  remove_script_host: false,
  document_base_url: window.location.origin + '/',
  toolbar_mode: 'floating',
  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
  file_picker_callback: async (cb, value, meta) => {
    if (meta.filetype === 'image') {
      targetField.value = 'content';
      fileManagerCallback.value = cb;
      await fetchFolders();
      showFileManagerModal.value = true;
    }
  },
};

const slugify = (text) => {
  if (!text) return '';
  return text.toString()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
};
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

watch(title, (newTitle) => {
  if (!slug.value) slug.value = slugify(newTitle);
});

const canSubmit = computed(() =>
  !!title.value?.trim() && !!description.value?.trim() && !!status.value
);

const onAvatarFilePicked = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;
  try {
    const fd = new FormData();
    fd.append('file', file);
    // fd.append('alt', title.value || file.name);
    // fd.append('title', title.value || file.name);

    const { data } = await api.post(`/api/wp-sites/${wpSiteId}/media`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    images.value = data?.source_url || '';
    featuredMediaId.value = data?.id || null;

    toast.success('Đã upload ảnh lên WordPress!');
  } catch (err) {
    console.error(err);
    toast.error('Upload ảnh lên WordPress thất bại.');
  } finally {
    e.target.value = ''; // reset input
  }
};

onMounted(async () => {
  try {
    // BE đã thêm _embed=1 trong getPostDetail nên FE đọc _embedded được luôn
    const { data } = await api.get(`/api/wp-sites/${wpSiteId}/posts/${wpPostId}`);
    article.value = data;

    title.value = data?.title?.rendered || '';
    content.value = data?.content?.raw || data?.content?.rendered || '';
    const meta = data?.meta || {};
    seoTitle.value = meta?.rank_math_title || '';
    description.value = stripHtml(meta?.rank_math_description) || stripHtml(data?.excerpt?.rendered) || '';
    slug.value = data?.slug || '';
    status.value = data?.status || 'draft';
    selectedCategories.value = Array.isArray(data?.categories) ? data.categories.slice() : [];
    focusKeywords.value = meta?.rank_math_focus_keyword || '';
    seoScore.value = meta?.rank_math_seo_score ?? null;

    const media = data?._embedded?.["wp:featuredmedia"]?.[0];
    images.value = media?.source_url || '';
  } catch (err) {
    console.error(err);
    error.value = 'Lỗi tải dữ liệu bài viết (proxy).';
    toast.error(error.value);
  } finally {
    loading.value = false;
  }

  try {
    const categoriesRes = await api.get(`/api/wp-sites/${wpSiteId}/categories`);
    categories.value = categoriesRes.data || [];
  } catch (err) {
    console.error('Lỗi khi tải danh mục:', err);
  } finally {
    categoriesLoading.value = false;
  }
});

const seoScoreDisplay = computed(() => {
  const n = Number(seoScore.value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
});

// ---------- Submit qua BE ----------
const handleSubmit = async () => {
  if (!canSubmit.value) {
    toast.error('Thiếu tiêu đề/Mô tả/Trạng thái.');
    return;
  }
  submitting.value = true;
  try {
    // Nếu để trống, cho seoTitle bám theo title để dễ sử dụng
    const finalSeoTitle = (seoTitle.value?.trim() || title.value?.trim() || '');

    const payload = {
      title: title.value?.trim(),
      seoTitle: finalSeoTitle,
      seoDescription: description.value?.trim(),   // Rank Math
      description: description.value?.trim(),      // excerpt
      content: content.value,
      slug: slug.value?.trim(),
      status: status.value,
      categoryIds: selectedCategories.value,
      featuredMediaId:  featuredMediaId.value,
      featuredImageUrl: images.value || null,      // BE sẽ resolve -> featured_media
      featuredMediaId: null,
      focusKeywords: focusKeywords.value?.trim(),
      tagIds: [],
    };

    await api.put(`/api/wp-sites/${wpSiteId}/posts/${wpPostId}`, payload);
    toast.success('✅ Đã cập nhật bài viết qua BE!');
  } catch (err) {
    console.error(err);
    toast.error('❌ Lỗi khi cập nhật bài viết (BE proxy).');
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
.form-select { cursor: pointer; }
</style>
